import { InjectionKey, Ref, computed, inject, ref, watch } from 'vue';

import { extractErrorMessage, useAlertsStore } from '@tallo/design-system';

import { InvestorOnboardingStep } from '../enums/investor-onboarding-step.enum';

/** Provided by the wizard layout: true while the assistant is still typing the step's copy */
export const onboardingTypingKey: InjectionKey<Ref<boolean>> = Symbol('onboardingTyping');
export const onboardingNavigationKey: InjectionKey<{
  goBack: () => void;
  /** Sends the user back to a step the server says is not done, saying why */
  returnTo: (step: InvestorOnboardingStep) => void;
}> = Symbol('onboardingNavigation');
/** Registers what Enter should do on the current step; null while the step cannot continue */
export const onboardingContinueActionKey: InjectionKey<(action: (() => void) | null) => void> =
  Symbol('onboardingContinueAction');

/**
 * The shell every wizard step shares: the layout's typing flag and Back, the Enter shortcut,
 * and one save path that reports a failure and keeps the user on the step so Continue retries.
 */
export function useOnboardingStep(canProceed: () => boolean) {
  const typing = inject(onboardingTypingKey, ref(false));
  const { goBack, returnTo } = inject(onboardingNavigationKey, { goBack: () => undefined, returnTo: () => undefined });
  const registerContinueAction = inject(onboardingContinueActionKey, () => undefined);
  const alerts = useAlertsStore();

  const isSaving = ref(false);
  const continueAction = ref<(() => void) | null>(null);
  const canContinue = computed(() => canProceed() && !typing.value && !isSaving.value);

  watch(
    [canContinue, continueAction],
    ([allowed, action]) => registerContinueAction(allowed && action ? action : null),
    { immediate: true },
  );

  async function save(run: () => Promise<void>, failureMessage: string): Promise<void> {
    if (!canContinue.value) return;

    isSaving.value = true;
    try {
      await run();
    } catch (error) {
      alerts.error(extractErrorMessage(error, failureMessage));
    } finally {
      isSaving.value = false;
    }
  }

  function onContinue(action: () => void): void {
    continueAction.value = action;
  }

  return { typing, goBack, returnTo, isSaving, canContinue, save, onContinue };
}
